import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import { selectFeatureFlags } from 'yoda-site-components/lib/selectors/ContextSelector';
import { selectedProductsForBundles } from '../../../selectors/Bundle/BundleProductOptionsSelector';
import { formErrorAnalytics } from '../../../actions/AnalyticsAction';

/**
 * Higher order component of bundle sku validation.
 * @param {String} wrappedComponentParam  Property of Wrapped component to perform wrapped component changes.
 */
const withBundleSkuValidation = (wrappedComponentParam = {}) => {
    // eslint-disable-line
    /**
     * @param {Object} WrappedComponent     React Component
     */
    return function Factory(WrappedComponent) {
        /* istanbul ignore next */
        class BundleSkuValidation extends Component {
            // eslint-disable-line
            static defaultProps = {
                selectedBundleSkus: {},
                selectedProtectionPlan: {},
                messagesTexts: {},
                deliveryDate: {},
                bundleQuantity: {},
                actions: {},
                featureFlags: {},
            };

            static propTypes = {
                selectedBundleSkus: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
                selectedProtectionPlan: PropTypes.oneOfType([PropTypes.object]),
                messagesTexts: PropTypes.oneOfType([PropTypes.object]),
                deliveryDate: PropTypes.oneOfType([PropTypes.object]),
                bundleQuantity: PropTypes.oneOfType([PropTypes.object]),
                actions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
                featureFlags: PropTypes.object,
            };

            getBundleSkuValidation = (triggerAnalyticsFlag = false) => {
                const addToCartFormValidationResult = this.validateNonApplianceATCForm(
                    triggerAnalyticsFlag,
                    this?.props?.featureFlags?.enableWarrantySection
                );
                return addToCartFormValidationResult;
            };

            validateNonApplianceATCForm = (triggerAnalyticsFlag = false, enableWarrantySection) => {
                const {
                    selectedBundleSkus = {},
                    messagesTexts: { ProtectionPlanMessages },
                    selectedProtectionPlan = {},
                    bundleQuantity,
                } = this.props;
                const errorMessages = [];

                let validationResult = {};
                const allbundleProductId = selectedBundleSkus && Object.keys(selectedBundleSkus);

                allbundleProductId &&
                    allbundleProductId.forEach((id) => {
                        const validateWarranty = get(
                            selectedBundleSkus[id],
                            'selectedProductLot[0].warranties',
                            []
                        ).filter(({ selected }) => !selected);
                        const {
                            optionsSelected = [],
                            dimensions = [],
                            isProductInStock,
                        } = selectedBundleSkus[id];

                        validationResult = {
                            ...validationResult,
                            [id]: {
                                isValid: true,
                                messages: [],
                            },
                        };

                        if (bundleQuantity[id] !== 0 && isProductInStock) {
                            /* istanbul ignore next  */
                            if (dimensions.length && dimensions.length !== optionsSelected.length) {
                                let nonSelectedDimension;
                                if (optionsSelected.length) {
                                    nonSelectedDimension = optionsSelected.reduce(
                                        (dimensionAcc, option) =>
                                            dimensionAcc.filter(
                                                (dimension) => dimension.id !== option.type
                                            ),
                                        dimensions
                                    );
                                } else {
                                    nonSelectedDimension = [...dimensions];
                                }

                                const dimensionErrorMessage = nonSelectedDimension.map(
                                    (dimension) => {
                                        const dimensionName = dimension.name.split('');
                                        const dimensionMessage =
                                            dimensionName[0].toUpperCase() +
                                            dimensionName.slice(1).join('');
                                        errorMessages.push({
                                            errorDescription: `Don't Forget To Select a ${dimensionMessage}`,
                                        });

                                        return {
                                            section: `${dimension.id}_MESSAGES_SECTION_${id}`,
                                            message: {
                                                id: `${id}_${dimension.id}`,
                                                title: `Please Select a ${dimensionMessage}`,
                                                type: 'warning',
                                            },
                                        };
                                    }
                                );

                                validationResult = {
                                    ...validationResult,
                                    [id]: {
                                        ...validationResult[id],
                                        isValid: false,
                                        messages: [
                                            ...validationResult[id].messages,
                                            ...dimensionErrorMessage,
                                        ],
                                    },
                                };
                            }

                            /* istanbul ignore else */
                            if (
                                validateWarranty.length &&
                                !selectedProtectionPlan[id] &&
                                enableWarrantySection
                            ) {
                                const protectionErrorMessage = [
                                    {
                                        section: `PROTECTION_PLAN_MESSAGES_SECTION_${id}`,
                                        message: {
                                            id: `PROTECTION_PLAN_${id}`,
                                            title: ProtectionPlanMessages
                                                ? ProtectionPlanMessages.requiredFieldMessage
                                                : '',
                                            type: 'warning',
                                        },
                                    },
                                ];
                                validationResult = {
                                    ...validationResult,
                                    [id]: {
                                        ...validationResult[id],
                                        isValid: false,
                                        messages: [
                                            ...validationResult[id].messages,
                                            ...protectionErrorMessage,
                                        ],
                                    },
                                };
                            }
                        }
                    });

                if (errorMessages.length && triggerAnalyticsFlag) {
                    this.props.actions.formErrorAnalytics(errorMessages);
                }

                const isEverySKUSelected = Object.keys(validationResult)
                    .map((key) => validationResult[key].isValid)
                    .every(Boolean);

                return {
                    validationResult,
                    isEverySKUSelected,
                };
            };

            render() {
                return <WrappedComponent getBundleSkuValidation={this.getBundleSkuValidation} />;
            }
        }

        const mapStateToProps = (state) => ({
            selectedBundleSkus: selectedProductsForBundles(state),
            featureFlags: selectFeatureFlags(state),
            deliveryDate: state.bundleDeliveryDate,
            selectedProtectionPlan: state.bundleProtectionPlan,
            messagesTexts: state.context && state.context.messagesTexts,
            bundleQuantity: state.bundleQuantity,
            deviceType: state?.context?.deviceType ? state.context.deviceType : {},
        });

        const mapDispatchToProps = (dispatch) => ({
            actions: bindActionCreators(
                {
                    /* Analytics Actions */
                    formErrorAnalytics,
                },
                dispatch
            ),
        });

        return connect(mapStateToProps, mapDispatchToProps)(BundleSkuValidation);
    };
};

export default withBundleSkuValidation;
